import { ForwardedRef, forwardRef, Fragment, useMemo } from "react";
import { Box, Stack } from "@mui/material";
import isEmpty from "lodash/isEmpty";
import SingleSelectTypeahead, {
  TSingleSelectTypeaheadOption,
  TSingleSelectTypeaheadProps,
} from "components/shared/SingleSelectTypeahead";
import { TAssignmentSelectProps } from "./AssignmentSelect.types";

const AssignmentSelect = forwardRef(
  <T extends unknown = string>(
    {
      testId,
      disabled,
      value,
      error,
      onChange,
      onBlur,
      getInitialAssignment,
      itemsSelectProps,
      itemRenderer,
    }: TAssignmentSelectProps<T>,
    ref: ForwardedRef<HTMLElement>,
  ) => {
    const addItem = (option: TSingleSelectTypeaheadOption | null) => {
      if (!option) {
        return;
      }

      onChange([
        ...value,
        {
          item: { label: option.label, value: option.value },
          assignment: getInitialAssignment(option.value),
        },
      ]);
    };

    const removeItem = (itemValue: string) =>
      onChange(value.filter(({ item }) => item.value !== itemValue));

    const addAssignment = (assignmentValue: T, itemValue: string) => {
      const itemIndex = value.findIndex(({ item }) => item.value === itemValue);

      const item = {
        ...value[itemIndex],
        assignment: { value: assignmentValue },
      };

      onChange([
        ...value.slice(0, itemIndex),
        item,
        ...value.slice(itemIndex + 1),
      ]);
    };

    const getError = (index: number) => error?.[index]?.assignment?.value;

    const handleChange = (
      ...args: Parameters<NonNullable<TSingleSelectTypeaheadProps["onChange"]>>
    ) => {
      const [, value] = args;

      addItem(value);
      itemsSelectProps.onChange?.(...args);
    };

    const filteredOptions = useMemo(() => {
      const selectedValues = new Set(value.map(({ item }) => item.value));

      return itemsSelectProps.options.filter(
        (option) => !selectedValues.has(option.value),
      );
    }, [itemsSelectProps.options, value]);

    return (
      <Box>
        <SingleSelectTypeahead
          // TODO (AM): change business specific naming for `data-qaid` since it is a shared component
          data-qaid={`${testId}-project-team-members-dropdown`}
          testId={testId}
          disabled={disabled}
          textFieldProps={{
            size: "small",
          }}
          {...itemsSelectProps}
          value={null} // force select to always remain empty and never display a selected value
          options={filteredOptions}
          onChange={handleChange}
          onBlur={onBlur}
          blurOnSelect
          ref={ref}
        />
        {!isEmpty(value) && (
          <Stack
            sx={{
              mt: 2,
              gap: 1,
            }}
          >
            {value.map((assignment, index) => (
              <Fragment key={assignment.item.value}>
                {itemRenderer({
                  value: assignment,
                  testId,
                  removeItem,
                  addAssignment,
                  disabled,
                  error: getError(index),
                  autoFocus: value.length - 1 === index,
                })}
              </Fragment>
            ))}
          </Stack>
        )}
      </Box>
    );
  },
);

export default AssignmentSelect as <T, O extends TSingleSelectTypeaheadOption>(
  props: TAssignmentSelectProps<T, O>,
  ref?: ForwardedRef<HTMLElement>,
) => ReturnType<typeof AssignmentSelect>;
